/**
 * PitchMovementChart - Scatter plot showing pitch movement profiles
 * Inspired by PitcherList's movement chart with rise/drop on axes
 * Shows each pitch type as a cluster with horizontal break vs induced vertical break
 * Hover over any dot to see detailed pitch metrics
 */
import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';

// Pitch type colors matching PitcherList style
const PITCH_COLORS = {
  FF: '#e74c3c', // Four-Seam - Red
  SI: '#e67e22', // Sinker - Orange
  FC: '#f1c40f', // Cutter - Yellow
  SL: '#9b59b6', // Slider - Purple
  ST: '#e84393', // Sweeper - Pink
  CU: '#2ecc71', // Curveball - Green
  KC: '#27ae60', // Knuckle Curve - Dark Green
  CH: '#3498db', // Changeup - Blue
  FS: '#1abc9c', // Splitter - Teal
  KN: '#95a5a6', // Knuckleball - Gray
  EP: '#7f8c8d', // Eephus - Dark Gray
};

const PITCH_NAMES = {
  FF: 'Four-Seam Fastball', SI: 'Sinker', FC: 'Cutter', SL: 'Slider',
  ST: 'Sweeper', CU: 'Curveball', KC: 'Knuckle-Curve', CH: 'Changeup',
  FS: 'Splitter', KN: 'Knuckleball', EP: 'Eephus', SV: 'Slurve',
  FA: 'Fastball', FO: 'Forkball', CS: 'Slow Curve',
};

const PitchMovementChart = ({ pitchData, size = 280, throws = 'R' }) => {
  // For LHP: arm side is catcher's left (negative pfxX), so labels flip
  const isLefty = (throws || 'R').toUpperCase() === 'L';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [hovered, setHovered] = useState(null);
  const svgRef = useRef(null);

  const { pitchPoints, scaleX, scaleY, maxBreak } = useMemo(() => {
    if (!pitchData?.pitchTypes) return { pitchPoints: [], scaleX: () => 0, scaleY: () => 0, maxBreak: 24 };

    const points = [];
    const totalPitches = Object.values(pitchData.pitchTypes).reduce((s, d) => s + (d.count || 0), 0);

    Object.entries(pitchData.pitchTypes).forEach(([type, data]) => {
      if (data.count > 0 && data.avgHorizBreak !== undefined) {
        const swings = (data.swingAndMiss || 0) + (data.fouls || 0) + (data.inPlay || 0);
        const whiffRate = swings > 0 ? (data.swingAndMiss / swings) * 100 : 0;
        const swingRate = data.count > 0 ? (swings / data.count) * 100 : 0;
        const csw = data.count > 0 ? ((data.calledStrikes || 0) + (data.swingAndMiss || 0)) / data.count * 100 : 0;
        const usage = totalPitches > 0 ? (data.count / totalPitches) * 100 : 0;
        const avgSpin = data.spinCount > 0 ? Math.round(data.totalSpin / data.spinCount) : (data.avgSpin || 0);
        points.push({
          type,
          name: PITCH_NAMES[type] || type,
          color: PITCH_COLORS[type] || '#888',
          hBreak: data.avgHorizBreak || 0,
          vBreak: data.avgVertBreak || 0,
          count: data.count,
          velo: data.avgVelo || 0,
          maxVelo: data.maxVelo || 0,
          spin: avgSpin,
          whiffRate: whiffRate.toFixed(1),
          swingRate: swingRate.toFixed(1),
          csw: csw.toFixed(1),
          usage: usage.toFixed(1),
        });
      }
    });

    const mb = Math.max(24, ...points.map(p => Math.max(Math.abs(p.hBreak), Math.abs(p.vBreak))));
    const padding = 30;
    const chartSize = size - padding * 2;

    return {
      pitchPoints: points,
      scaleX: (val) => padding + ((val + mb) / (2 * mb)) * chartSize,
      scaleY: (val) => padding + ((mb - val) / (2 * mb)) * chartSize,
      maxBreak: mb,
    };
  }, [pitchData, size]);

  const handleMouseEnter = useCallback((e, point) => {
    setHovered({ ...point, mouseX: e.clientX, mouseY: e.clientY });
  }, []);

  const handleMouseMove = useCallback((e, point) => {
    setHovered(prev => prev ? { ...prev, mouseX: e.clientX, mouseY: e.clientY } : null);
  }, []);

  const handleMouseLeave = useCallback(() => setHovered(null), []);

  if (pitchPoints.length === 0) {
    return (
      <Box sx={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">No movement data</Typography>
      </Box>
    );
  }

  const mid = size / 2;
  const tooltipWidth = 168;
  const tooltipHeight = 240;
  const tooltipLeft = hovered
    ? (hovered.mouseX + tooltipWidth + 16 > window.innerWidth ? hovered.mouseX - tooltipWidth - 10 : hovered.mouseX + 14)
    : 0;
  const tooltipTop = hovered
    ? (hovered.mouseY + tooltipHeight + 16 > window.innerHeight ? hovered.mouseY - tooltipHeight - 10 : hovered.mouseY + 10)
    : 0;

  return (
    <Box sx={{ position: 'relative', display: 'block', userSelect: 'none' }}>
      <svg
        ref={svgRef}
        width="100%"
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ display: 'block', cursor: 'crosshair' }}
      >
        <rect width={size} height={size} fill={isDark ? '#151a2e' : '#f5f7fa'} rx={8} />

        {[6, 12, 18, 24].filter(r => r <= maxBreak).map(r => (
          <circle key={r} cx={mid} cy={mid} r={(r / maxBreak) * (size / 2 - 30)}
            fill="none" stroke={isDark ? '#2a3050' : '#dde1e8'} strokeWidth={0.5} strokeDasharray="3,3" />
        ))}

        <line x1={30} y1={mid} x2={size - 30} y2={mid} stroke={isDark ? '#3a4060' : '#c0c8d4'} strokeWidth={1} />
        <line x1={mid} y1={30} x2={mid} y2={size - 30} stroke={isDark ? '#3a4060' : '#c0c8d4'} strokeWidth={1} />

        <text x={mid} y={16} textAnchor="middle" fill={isDark ? '#8899bb' : '#667'} fontSize={11} fontWeight={600}>Rise</text>
        <text x={mid} y={size - 6} textAnchor="middle" fill={isDark ? '#8899bb' : '#667'} fontSize={11} fontWeight={600}>Drop</text>
        <text x={10} y={mid + 4} textAnchor="middle" fill={isDark ? '#8899bb' : '#667'} fontSize={10} fontWeight={600}>{isLefty ? 'Arm' : 'Glove'}</text>
        <text x={size - 10} y={mid + 4} textAnchor="middle" fill={isDark ? '#8899bb' : '#667'} fontSize={10} fontWeight={600}>{isLefty ? 'Glove' : 'Arm'}</text>

        {[-18, -12, -6, 6, 12, 18].filter(v => Math.abs(v) <= maxBreak).map(v => (
          <React.Fragment key={`tick-${v}`}>
            <text x={scaleX(v)} y={mid + 14} textAnchor="middle" fill={isDark ? '#556' : '#999'} fontSize={8}>{Math.abs(v)}"</text>
            <text x={mid + 14} y={scaleY(v) + 3} textAnchor="start" fill={isDark ? '#556' : '#999'} fontSize={8}>{Math.abs(v)}"</text>
          </React.Fragment>
        ))}

        {pitchPoints.map((p) => {
          const r = Math.min(10, 4 + p.count / 12);
          const isHov = hovered?.type === p.type;
          return (
            <g key={p.type} onMouseEnter={(e) => handleMouseEnter(e, p)} onMouseMove={(e) => handleMouseMove(e, p)} onMouseLeave={handleMouseLeave} style={{ cursor: 'pointer' }}>
              <circle cx={scaleX(p.hBreak)} cy={scaleY(p.vBreak)} r={isHov ? r + 8 : Math.min(16, 6 + p.count / 8)} fill={p.color} opacity={isHov ? 0.35 : 0.2} />
              <circle cx={scaleX(p.hBreak)} cy={scaleY(p.vBreak)} r={isHov ? r + 2 : r} fill={p.color} stroke={isDark ? '#fff' : '#222'} strokeWidth={isHov ? 2.5 : 1.5} opacity={0.95} />
              <text x={scaleX(p.hBreak)} y={scaleY(p.vBreak) - (isHov ? r + 6 : r) - 4} textAnchor="middle" fill={p.color} fontSize={isHov ? 10 : 9} fontWeight={700}>{p.type}</text>
            </g>
          );
        })}
      </svg>

      {/* Hover Tooltip — position:fixed so it renders above all overflow:hidden parents */}
      {hovered && (
        <Box sx={{
          position: 'fixed', left: tooltipLeft, top: tooltipTop, zIndex: 9999, pointerEvents: 'none',
          minWidth: tooltipWidth,
          bgcolor: isDark ? alpha('#0d1117', 0.97) : alpha('#fff', 0.97),
          border: `1.5px solid ${alpha(hovered.color, 0.65)}`,
          borderRadius: 1.5,
          boxShadow: `0 4px 20px ${alpha(hovered.color, 0.22)}`,
          p: 1.25,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, pb: 0.6, borderBottom: `1px solid ${alpha(hovered.color, 0.2)}` }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: hovered.color, flexShrink: 0 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: hovered.color, lineHeight: 1 }}>{hovered.name}</Typography>
          </Box>
          {[
            { label: 'Avg Velo', value: hovered.velo ? `${hovered.velo} mph` : '—' },
            { label: 'Max Velo', value: hovered.maxVelo > 0 ? `${hovered.maxVelo} mph` : '—' },
            { label: 'Spin Rate', value: hovered.spin > 0 ? `${hovered.spin.toLocaleString()} rpm` : '—' },
            { label: 'H-Break', value: `${hovered.hBreak > 0 ? '+' : ''}${(hovered.hBreak || 0).toFixed(1)}"` },
            { label: 'V-Break', value: `${hovered.vBreak > 0 ? '+' : ''}${(hovered.vBreak || 0).toFixed(1)}"` },
            { label: 'Usage', value: `${hovered.usage}%` },
            { label: 'Whiff%', value: `${hovered.whiffRate}%` },
            { label: 'Swing%', value: `${hovered.swingRate}%` },
            { label: 'CSW%', value: `${hovered.csw}%` },
            { label: 'Pitches', value: hovered.count },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.18 }}>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'text.primary', fontFamily: '"Roboto Mono", monospace' }}>{value}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, justifyContent: 'center' }}>
        {pitchPoints.map(p => (
          <Box key={p.type} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.6, py: 0.25, borderRadius: 1, bgcolor: hovered?.type === p.type ? alpha(p.color, 0.1) : 'transparent', transition: 'background 0.15s' }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color }} />
            <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              {p.name} <span style={{ opacity: 0.55 }}>({p.usage}%)</span>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PitchMovementChart;
